import auth0 from "auth0-js";
import { AUTH_CONFIG } from "./auth0-variables";
import EventEmitter from "eventemitter3";
import decode from "jwt-decode";
import IdTokenVerifier from "idtoken-verifier";
import router from "../router/index";

export default class AuthService {
  authenticated = this.isAuthenticated();
  authNotifier = new EventEmitter();

  constructor() {
    this.login = this.login.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.setSession = this.setSession.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getIdToken = this.getIdToken.bind(this);
    this.logout = this.logout.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.verifyToken = this.verifyToken.bind(this);
    this.userInfo = this.getUserInfo.bind(this);
  }

  webAuth = new auth0.WebAuth({
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientId,
    redirectUri: AUTH_CONFIG.callbackUrl,
    audience: AUTH_CONFIG.audience,
    responseType: "token id_token",
    scope: "openid email profile"
  });

  verifier = new IdTokenVerifier({
    issuer: `https://${AUTH_CONFIG.domain}/`,
    audience: AUTH_CONFIG.clientId,
    jwksCache: new Map(), // in-memory cache
    jwksURI: `https://${AUTH_CONFIG.domain}/.well-known/jwks.json`
  });

  login() {
    // force the IdP to login again
    this.webAuth.authorize({ prompt: "login" });
  }

  getUserInfo(token) {
    return new Promise((resolve, reject) => {
      this.webAuth.client.userInfo(token, (err, userInfo) => {
        if (userInfo) {
          resolve(userInfo);
        } else if (err) {
          reject(err);
        }
      });
    });
  }

  handleAuthentication() {
    this.webAuth.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
      } else if (err) {
        router.replace({
          name: "home",
          params: { error: { type: err.error, message: err.errorDescription } }
        });
      }
      router.replace("home");
    });
  }

  setSession(authResult) {
    localStorage.setItem("access_token", authResult.accessToken);
    localStorage.setItem("id_token", authResult.idToken);
    this.authNotifier.emit("authChange", { authenticated: true });
  }

  getAccessToken(shouldRaise = true) {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken && shouldRaise) {
      throw new Error("No access token found");
    }
    return accessToken;
  }

  getIdToken(shouldRaise = true) {
    const idToken = localStorage.getItem("id_token");
    if (!idToken && shouldRaise) {
      throw new Error("No ID token found");
    }
    return idToken;
  }

  verifyToken() {
    const idToken = this.getIdToken();
    const nonce = decode(idToken)["nonce"];

    // This method will decode the token, verify the issuer, audience,
    // expiration, algorithm and nonce claims and after that will verify
    // the token signature.
    return this.verifier.verify(idToken, nonce, error => {
      if (error) {
        console.error("JWT token is either invalid or has expired.");
        // Logout the user
        this.logout();
      }
    });
  }

  logout() {
    // Clear access token and ID token from local storage
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    this.authNotifier.emit("authChange", false);
    // Navigate to the home route
    router.replace("home");
  }

  isAuthenticated() {
    try {
      this.verifyToken();
    } catch (e) {
      // silent catch
    }
    return !!(this.getIdToken(false) && this.getAccessToken(false));
  }
}
